import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogTrigger
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Building2, 
  Plus,
  MapPin,
  Phone,
  Clock,
  Edit,
  Trash2,
  Star,
  Users,
  Calendar,
  Loader2,
  CheckCircle,
  ArrowLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MultiLocationManager = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    postal_code: '',
    phone: '',
    email: '',
    description: '',
    is_primary: false,
    opening_hours: {
      monday: { open: '09:00', close: '18:00', closed: false },
      tuesday: { open: '09:00', close: '18:00', closed: false },
      wednesday: { open: '09:00', close: '18:00', closed: false },
      thursday: { open: '09:00', close: '18:00', closed: false },
      friday: { open: '09:00', close: '18:00', closed: false },
      saturday: { open: '09:00', close: '13:00', closed: false },
      sunday: { open: '', close: '', closed: true }
    }
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchLocations();
  }, [token]);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/professional/locations`, { headers });
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      city: '',
      postal_code: '',
      phone: '',
      email: '',
      description: '',
      is_primary: false,
      opening_hours: {
        monday: { open: '09:00', close: '18:00', closed: false },
        tuesday: { open: '09:00', close: '18:00', closed: false },
        wednesday: { open: '09:00', close: '18:00', closed: false },
        thursday: { open: '09:00', close: '18:00', closed: false },
        friday: { open: '09:00', close: '18:00', closed: false },
        saturday: { open: '09:00', close: '13:00', closed: false },
        sunday: { open: '', close: '', closed: true }
      }
    });
    setEditingLocation(null);
  };

  const openAddDialog = () => {
    resetForm();
    setShowAddDialog(true);
  };

  const openEditDialog = (location) => {
    setFormData({
      name: location.name || '',
      address: location.address || '',
      city: location.city || '',
      postal_code: location.postal_code || '',
      phone: location.phone || '',
      email: location.email || '',
      description: location.description || '',
      is_primary: location.is_primary || false,
      opening_hours: location.opening_hours || formData.opening_hours
    });
    setEditingLocation(location);
    setShowAddDialog(true);
  };

  const saveLocation = async () => {
    if (!formData.name || !formData.address || !formData.city) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    setSaving(true);
    try {
      if (editingLocation) {
        await axios.put(
          `${API_URL}/api/professional/locations/${editingLocation.id}`,
          formData,
          { headers }
        );
        toast.success('Établissement mis à jour');
      } else {
        await axios.post(
          `${API_URL}/api/professional/locations`,
          formData,
          { headers }
        );
        toast.success('Établissement ajouté');
      }
      
      setShowAddDialog(false);
      resetForm();
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const deleteLocation = async (locationId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet établissement ?')) {
      return;
    }

    try {
      await axios.delete(
        `${API_URL}/api/professional/locations/${locationId}`,
        { headers }
      );
      toast.success('Établissement supprimé');
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const setPrimaryLocation = async (locationId) => {
    try {
      await axios.post(
        `${API_URL}/api/professional/locations/${locationId}/set-primary`,
        {},
        { headers }
      );
      toast.success('Établissement principal défini');
      fetchLocations();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const days = [
    { key: 'monday', label: 'Lundi' },
    { key: 'tuesday', label: 'Mardi' },
    { key: 'wednesday', label: 'Mercredi' },
    { key: 'thursday', label: 'Jeudi' },
    { key: 'friday', label: 'Vendredi' },
    { key: 'saturday', label: 'Samedi' },
    { key: 'sunday', label: 'Dimanche' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="multi-location-page">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/dashboard/professional" 
            className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour au tableau de bord
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3">
                <Building2 className="w-8 h-8 text-primary" />
                Mes Établissements
              </h1>
              <p className="text-slate-500 mt-1">
                Gérez vos différents lieux de consultation
              </p>
            </div>
            <Button onClick={openAddDialog} className="gap-2">
              <Plus className="w-4 h-4" />
              Ajouter un établissement
            </Button>
          </div>
        </div>

        {/* Locations List */}
        {locations.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">
                Aucun établissement
              </h3>
              <p className="text-slate-500 mb-6">
                Ajoutez votre premier lieu de consultation
              </p>
              <Button onClick={openAddDialog} className="gap-2">
                <Plus className="w-4 h-4" />
                Ajouter un établissement
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {locations.map((location) => (
              <Card 
                key={location.id} 
                className={`relative ${location.is_primary ? 'border-primary border-2' : ''}`}
              >
                {location.is_primary && (
                  <Badge className="absolute -top-2 -right-2 bg-primary">
                    <Star className="w-3 h-3 mr-1" />
                    Principal
                  </Badge>
                )}
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-primary" />
                    {location.name}
                  </CardTitle>
                  <CardDescription>{location.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-start gap-2 text-sm text-slate-600">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      {location.address}<br />
                      {location.postal_code} {location.city}
                    </span>
                  </div>
                  
                  {location.phone && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Phone className="w-4 h-4" />
                      {location.phone}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Clock className="w-4 h-4" />
                    <span>Horaires configurés</span>
                  </div>
                  
                  <div className="flex items-center gap-2 pt-4 border-t">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openEditDialog(location)}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Modifier
                    </Button>
                    
                    {!location.is_primary && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setPrimaryLocation(location.id)}
                        >
                          <Star className="w-4 h-4 mr-1" />
                          Définir principal
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => deleteLocation(location.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                {editingLocation ? 'Modifier l\'établissement' : 'Ajouter un établissement'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Nom de l'établissement *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Cabinet Dr. Dupont"
                  />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+33 1 23 45 67 89"
                  />
                </div>
              </div>

              <div>
                <Label>Adresse *</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Rue de la Santé"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Ville *</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Paris"
                  />
                </div>
                <div>
                  <Label>Code postal</Label>
                  <Input
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    placeholder="75001"
                  />
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description du cabinet..."
                  rows={2}
                />
              </div>

              {/* Opening Hours */}
              <div>
                <Label className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4" />
                  Horaires d'ouverture
                </Label>
                <div className="space-y-2">
                  {days.map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-3 p-2 bg-slate-50 rounded">
                      <span className="w-24 text-sm font-medium">{label}</span>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!formData.opening_hours[key]?.closed}
                          onChange={(e) => setFormData({
                            ...formData,
                            opening_hours: {
                              ...formData.opening_hours,
                              [key]: { ...formData.opening_hours[key], closed: !e.target.checked }
                            }
                          })}
                          className="rounded"
                        />
                        <span className="text-sm">Ouvert</span>
                      </label>
                      {!formData.opening_hours[key]?.closed && (
                        <>
                          <Input
                            type="time"
                            value={formData.opening_hours[key]?.open || '09:00'}
                            onChange={(e) => setFormData({
                              ...formData,
                              opening_hours: {
                                ...formData.opening_hours,
                                [key]: { ...formData.opening_hours[key], open: e.target.value }
                              }
                            })}
                            className="w-28"
                          />
                          <span>à</span>
                          <Input
                            type="time"
                            value={formData.opening_hours[key]?.close || '18:00'}
                            onChange={(e) => setFormData({
                              ...formData,
                              opening_hours: {
                                ...formData.opening_hours,
                                [key]: { ...formData.opening_hours[key], close: e.target.value }
                              }
                            })}
                            className="w-28"
                          />
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Annuler
              </Button>
              <Button onClick={saveLocation} disabled={saving}>
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                {editingLocation ? 'Mettre à jour' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MultiLocationManager;
